import { AnswerPayload, Question, QuestionType } from './questions.type';

export interface SurveyResponseRequest {
    type: 'survey-response';
    attributes: SurveyResponse;
}

export interface UpdateSurveyResponseAnswerRequest {
    type: 'survey-response-answer';
    attributes: UpdateSurveyResponseAnswer;
}

export interface GetSurveyResponseAnswerResponse {
    type: 'survey-response-answer';
    id: number;
    attributes: SurveyResponseAnswers;
}

export interface SurveyQuestionsResponse {
    type: 'survey';
    attributes: SurveyDetails;
}
/****************************/

export enum SurveyType {
    REVIEW = 'REVIEW',
    CONTENT = 'CONTENT',
    REVIEW_GUEST_AIRBNB = 'REVIEW_GUEST_AIRBNB',
    REVIEW_HOST_AIRBNB = 'REVIEW_HOST_AIRBNB',
    REVIEW_GUEST_BOOKING = 'REVIEW_GUEST_BOOKING',
    REVIEW_GUEST_PMS_EXTERNAL = 'REVIEW_GUEST_PMS_EXTERNAL',
}

export enum SurveyName {
    REVIEW_SURVEY_OLD = 'REVIEW_SURVEY_OLD',
    REVIEW_SURVEY_NEW = 'REVIEW_SURVEY_NEW',
    REVIEW_SINGLE_PAGE = 'REVIEW_SINGLE_PAGE',
    REVIEW_NEGATIVE_DISPOSITION = 'REVIEW_NEGATIVE_DISPOSITION',
    REVIEW_POSITIVE_DISPOSITION = 'REVIEW_POSITIVE_DISPOSITION',
    REVIEW_PAL_NEGATIVE_DISPOSITION = 'REVIEW_PAL_NEGATIVE_DISPOSITION',
    REVIEW_PAL_POSITIVE_DISPOSITION = 'REVIEW_PAL_POSITIVE_DISPOSITION',
    REVIEW_PAL_POSITIVE_DISPOSITION_WITH_CONTENT = 'REVIEW_PAL_POSITIVE_DISPOSITION_WITH_CONTENT',
    REVIEW_PAL_NEGATIVE_DISPOSITION_WITH_CONTENT = 'REVIEW_PAL_NEGATIVE_DISPOSITION_WITH_CONTENT',
    REVIEW_PRIVATE_FEEDBACK_FIRST = 'REVIEW_PRIVATE_FEEDBACK_FIRST',
    REVIEW_GUEST_AIRBNB = 'REVIEW_GUEST_AIRBNB',
    REVIEW_HOST_AIRBNB = 'REVIEW_HOST_AIRBNB',
    REVIEW_BOOKING_AIRBNB = 'REVIEW_GUEST_BOOKING',
    PMS_EXTERNAL_GUEST = 'PMS_EXTERNAL_GUEST',
    DURING_STAY_CONTENT = 'DURING_STAY_CONTENT',
}

export enum ReviewSurveyId {
    SURVEY_GUEST_AIRBNB = 8,
    SURVEY_HOST_AIRBNB = 9,
    SURVEY_GUEST_BOOKING = 10,
    SURVEY_GUEST_PMS_EXTERNAL = 13,
}

export type SurveyDetails<T extends SurveyType = SurveyType.REVIEW> = {
    survey_id: string;
    name: string;
    description: string;
    sections: Section[];
    type: T;
} & (T extends SurveyType.REVIEW | SurveyType.CONTENT
    ? {
          context: ReviewSurveyContext & {
              prior_content?: Array<{
                  review_content_id: number;
                  s3_key: string;
                  description?: string | null;
                  read_url?: string | null;
              }>;
              prior_tip?: string | null;
          };
      }
    : {});

export interface UpdateSurveyResponseAnswer {
    new_value: string;
    old_value: string;
    survey_response_answers: SurveyResponseAnswers;
}

export interface SurveyTestInformation {
    feature_flags: Array<{
        name: string;
        variant: string;
    }>;
    star_rating: string;
}

export interface SurveyResponse {
    first_name: string;
    last_name: string;
    email: string;
    type: SurveyType;
    admin_reservation_id: number;
    admin_unit_id: number;
    source: string;
    review_request_origin?: string;
    survey_response_answers: Array<SurveyResponseAnswers>;
    survey_test_information?: SurveyTestInformation;
}

export type SurveyResponseAnswers = {
    survey_response_answers_id: number;
    survey_response_id: string;
    question_id: number;
    question_title?: string;
    type: QuestionType;
    category: string;
    answer: AnswerPayload;
    created_at?: string;
    updated_at?: string;
};

export interface Section {
    section_id: number;
    title: string;
    description: string;
    section_questions: Array<Question>;
}

interface ReviewSurveyContext {
    unit: {
        admin_unit_id: number;
        name: string;
        state: string;
        country_code: string;
        city: string;
        img: string;
    };
    reservation: {
        admin_reservation_id: number;
        first_name: string;
        last_name: string;
        email: string;
    };
}

export interface GeneralInformation {
    survey_id: number;
    first_name: string;
    last_name: string;
    email: string;
    admin_reservation_id: number;
    admin_unit_id: number;
}

export enum SurveySources {
    GUEST = 'GUEST',
    BULK_IMPORT = 'BULK_IMPORT',
    MIGRATION = 'MIGRATION',
    CHANNELS = 'CHANNELS',
    PAL_GUEST = 'PAL_GUEST',
    PMS_EXTERNAL_GUEST = 'PMS_EXTERNAL_GUEST',
    PRE_REVIEW_PAL_GUEST = 'PRE_REVIEW_PAL_GUEST', // During-stay content collection
    PAL_GUEST_WITH_CONTENT = 'PAL_GUEST_WITH_CONTENT', // Review after during-stay content

    LEGACY_UNIT_GUEST = 'LEGACY_UNIT_GUEST', // Before GUEST
    LEGACY_UNIT_MIGRATION = 'LEGACY_UNIT_MIGRATION', // Before MIGRATION
    LEGACY_UNIT_BULK_IMPORT = 'LEGACY_UNIT_BULK_IMPORT', // Before BULK_IMPORT

    PAL_BULK_IMPORT = 'PAL_BULK_IMPORT', // New bulk import source for PAL units
}

/** Sources for PAL + PMS External (filter toggle in UI). */
export const PAL_SOURCES: SurveySources[] = [
    SurveySources.PAL_GUEST,
    SurveySources.PAL_GUEST_WITH_CONTENT,
    SurveySources.PMS_EXTERNAL_GUEST,
    SurveySources.LEGACY_UNIT_MIGRATION,
    SurveySources.LEGACY_UNIT_GUEST,
    SurveySources.LEGACY_UNIT_BULK_IMPORT,
    SurveySources.PAL_BULK_IMPORT,
];

/**
 * Guest survey sources eligible for unit-level review summary.
 * Excludes OTA CHANNELS (host/guest mix) and PRE_REVIEW_PAL_GUEST (content-only).
 * BULK_IMPORT / LEGACY_UNIT_BULK_IMPORT may also appear with an id suffix — handled in SQL LIKE.
 */
export const UNIT_REVIEW_SUMMARY_SOURCES: SurveySources[] = [
    SurveySources.GUEST,
    SurveySources.BULK_IMPORT,
    SurveySources.MIGRATION,
    SurveySources.PAL_GUEST,
    SurveySources.PAL_GUEST_WITH_CONTENT,
    SurveySources.PMS_EXTERNAL_GUEST,
    SurveySources.LEGACY_UNIT_GUEST,
    SurveySources.LEGACY_UNIT_MIGRATION,
    SurveySources.LEGACY_UNIT_BULK_IMPORT,
    SurveySources.PAL_BULK_IMPORT,
];
